import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Download, RefreshCw } from "lucide-react";
import XLSXStyle from "xlsx-js-style";
import { saveAs } from "file-saver";
import { getAllAttendance } from "../../Api/attendanceApi";
import { getAllLeavesApi } from "../../Api/leaveApi";

// ───────────── data (loaded once, shared by every open for a minute) ─────────────
let cache = { at: 0, promise: null };
const loadData = (force) => {
  if (!force && cache.promise && Date.now() - cache.at < 60000) return cache.promise;
  cache = {
    at: Date.now(),
    promise: Promise.all([
      getAllAttendance(),
      getAllLeavesApi().catch(() => ({ success: false, data: [] })),
    ]).then(([a, l]) => ({
      attendance: a && a.success ? a.data || [] : [],
      leaves: l && l.success ? l.data || [] : [],
    })),
  };
  return cache.promise;
};

// ───────────── helpers (all dates are India time) ─────────────
const IST = "Asia/Kolkata";
const pad = (n) => String(n).padStart(2, "0");
const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: IST });
const hhmm = (t) =>
  t ? new Date(t).toLocaleTimeString("en-GB", { timeZone: IST, hour: "2-digit", minute: "2-digit", hour12: false }) : "";
const to12h = (mins) => {
  const h = Math.floor(mins / 60) % 24;
  const m = Math.round(mins % 60);
  return `${h % 12 || 12}:${pad(m)} ${h >= 12 ? "PM" : "AM"}`;
};
const mins = (t) => {
  const [h, m] = hhmm(t).split(":").map(Number);
  return h * 60 + m;
};
const fmtHours = (h) => `${Math.floor(h)}h ${pad(Math.round((h % 1) * 60))}m`;
const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
};
const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};

const KIND = {
  present: { label: "Present", cell: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  late: { label: "Late", cell: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  absent: { label: "Absent", cell: "bg-red-100 text-red-700", dot: "bg-red-500" },
  leave: { label: "Leave", cell: "bg-sky-100 text-sky-700", dot: "bg-sky-500" },
  off: { label: "Week off", cell: "bg-gray-100 text-gray-400", dot: "bg-gray-300" },
  pending: { label: "Today", cell: "bg-white text-cyan-700 ring-2 ring-cyan-300", dot: "bg-cyan-400" },
  future: { label: "Upcoming", cell: "bg-white text-gray-300 border border-dashed border-gray-200", dot: "bg-gray-200" },
};

// one month for one employee → per-day list + totals
const buildMonth = (empId, ym, attendance, leaves) => {
  const [y, m] = ym.split("-").map(Number);
  const daysIn = new Date(y, m, 0).getDate();
  const today = todayIST();
  const recs = {};
  attendance.forEach((r) => {
    if (r.user?._id === empId && r.date && r.date.startsWith(ym)) recs[r.date] = r;
  });
  const myLeaves = leaves.filter((l) => (l.user?._id || l.user) === empId && l.status === "Approved");
  const onLeave = (d) => myLeaves.some((l) => l.fromDate <= d && l.toDate >= d);

  const days = [];
  for (let i = 1; i <= daysIn; i++) {
    const date = `${ym}-${pad(i)}`;
    const dow = new Date(`${date}T00:00:00`).getDay(); // 0 = Sunday
    const rec = recs[date];
    const present = rec && rec.status !== "Absent" && (rec.checkIn?.time || (rec.statusSource === "admin" && rec.status));
    let kind;
    if (present) kind = rec.status === "Late" ? "late" : "present";
    else if (onLeave(date)) kind = "leave";
    else if (rec && rec.status === "Absent") kind = "absent";
    else if (dow === 0) kind = "off";
    else if (date > today) kind = "future";
    else if (date === today) kind = "pending";
    else kind = "absent";
    const inT = rec?.checkIn?.time || null;
    const outT = rec?.checkOut?.time || null;
    const hours = inT && outT && new Date(outT) > new Date(inT) ? (new Date(outT) - new Date(inT)) / 36e5 : null;
    days.push({ date, day: i, dow, kind, inT, outT, hours, flagged: !!(rec && rec.status === "Absent" && inT) });
  }

  const count = (k) => days.filter((d) => d.kind === k).length;
  const presentDays = count("present") + count("late");
  const absentDays = count("absent");
  const counted = presentDays + absentDays; // days that count towards the percentage
  const withHours = days.filter((d) => d.hours !== null);
  const totalHours = withHours.reduce((s, d) => s + d.hours, 0);
  const inMins = days.filter((d) => (d.kind === "present" || d.kind === "late") && d.inT).map((d) => mins(d.inT));
  return {
    days,
    present: presentDays,
    late: count("late"),
    absent: absentDays,
    leave: count("leave"),
    counted,
    pct: counted ? Math.round((presentDays / counted) * 100) : null,
    totalHours,
    avgHours: withHours.length ? totalHours / withHours.length : null,
    avgIn: inMins.length ? inMins.reduce((a, b) => a + b, 0) / inMins.length : null,
  };
};

// ───────────── small pieces ─────────────
const Tile = ({ label, value, sub, tone }) => (
  <div className={`rounded-xl border p-3 ${tone}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wider opacity-70">{label}</p>
    <p className="text-2xl font-extrabold leading-tight mt-0.5">{value}</p>
    {sub ? <p className="text-[11px] opacity-70 mt-0.5">{sub}</p> : null}
  </div>
);

const Ring = ({ pct }) => {
  const r = 38;
  const c = 2 * Math.PI * r;
  const v = pct === null ? 0 : pct;
  const colour = pct === null ? "#cbd5e1" : pct >= 90 ? "#10b981" : pct >= 75 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative w-28 h-28 shrink-0">
      <svg viewBox="0 0 100 100" className="w-28 h-28 -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#e2e8f0" strokeWidth="10" />
        <circle
          cx="50" cy="50" r={r} fill="none" stroke={colour} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * v) / 100} style={{ transition: "stroke-dashoffset .6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-extrabold text-gray-900">{pct === null ? "—" : `${pct}%`}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Attendance</span>
      </div>
    </div>
  );
};

// ───────────── main component ─────────────
export default function EmployeeMonthSummary({ employee }) {
  const nowYm = todayIST().slice(0, 7);
  const [ym, setYm] = useState(nowYm);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback((force) => {
    setError(false);
    setData(null);
    loadData(force).then(setData).catch(() => setError(true));
  }, []);
  useEffect(() => { load(false); }, [load]);

  const month = useMemo(
    () => (data ? buildMonth(employee._id, ym, data.attendance, data.leaves) : null),
    [data, employee._id, ym]
  );

  const download = () => {
    if (!month) return;
    const rows = [
      [`${employee.name} - ${monthLabel(ym)}`],
      [`Present ${month.present} (late ${month.late})   Absent ${month.absent}   Leave ${month.leave}   Attendance ${month.pct === null ? "-" : month.pct + "%"}   Hours ${fmtHours(month.totalHours)}`],
      [],
      ["Date", "Day", "Status", "Check In", "Check Out", "Hours"],
      ...month.days.map((d) => [
        d.date,
        new Date(`${d.date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short" }),
        d.flagged ? "Absent (checked in late)" : KIND[d.kind].label,
        hhmm(d.inT),
        d.inT ? (d.outT ? hhmm(d.outT) : "--") : "",
        d.hours !== null ? fmtHours(d.hours) : "",
      ]),
    ];
    const ws = XLSXStyle.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 13 }, { wch: 7 }, { wch: 24 }, { wch: 10 }, { wch: 11 }, { wch: 9 }];
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } }];
    const at = (r, c) => ws[XLSXStyle.utils.encode_cell({ r, c })];
    if (at(0, 0)) at(0, 0).s = { font: { bold: true, sz: 14 } };
    for (let c = 0; c < 6; c++) at(3, c).s = { fill: { fgColor: { rgb: "1F3864" } }, font: { bold: true, color: { rgb: "FFFFFF" } }, alignment: { horizontal: "center" } };
    const fills = { present: "C6EFCE", late: "FFEB9C", absent: "FFC7CE", leave: "BDD7EE", off: "E7E6E6" };
    month.days.forEach((d, i) => {
      const f = fills[d.kind];
      if (f) for (let c = 0; c < 6; c++) { const x = at(4 + i, c); if (x) x.s = { fill: { fgColor: { rgb: f } } }; }
    });
    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, ws, "Month");
    const buf = XLSXStyle.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `${(employee.name || "employee").replace(/\s+/g, "_")}_${ym}.xlsx`
    );
  };

  // Monday-first calendar: blanks before day 1
  const lead = month ? (month.days[0].dow + 6) % 7 : 0;

  return (
    <div className="p-5 sm:p-6">
      <div>
        {/* title + month picker */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h4 className="text-base font-bold text-gray-900">Monthly attendance</h4>
            <p className="text-xs text-gray-400">Working days are Monday to Saturday</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-xl border border-gray-200 bg-white">
              <button onClick={() => setYm(shiftMonth(ym, -1))} className="p-2 text-gray-500 hover:text-indigo-600" title="Previous month">
                <ChevronLeft size={18} />
              </button>
              <span className="px-2 text-sm font-semibold text-gray-800 min-w-[120px] text-center">{monthLabel(ym)}</span>
              <button
                onClick={() => setYm(shiftMonth(ym, 1))}
                disabled={ym >= nowYm}
                className="p-2 text-gray-500 hover:text-indigo-600 disabled:opacity-30 disabled:hover:text-gray-500"
                title="Next month"
              >
                <ChevronRight size={18} />
              </button>
            </div>
            {ym !== nowYm && (
              <button onClick={() => setYm(nowYm)} className="text-xs font-semibold text-cyan-600 hover:underline">This month</button>
            )}
            <button
              onClick={download}
              disabled={!month}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold disabled:opacity-40"
              title="Download this month as Excel"
            >
              <Download size={14} /> Excel
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-center justify-between">
            Could not load attendance.
            <button onClick={() => load(true)} className="flex items-center gap-1 font-semibold underline"><RefreshCw size={14} /> Retry</button>
          </div>
        )}

        {!data && !error && (
          <div className="animate-pulse space-y-3">
            <div className="h-28 rounded-xl bg-gray-100" />
            <div className="h-32 rounded-xl bg-gray-100" />
          </div>
        )}

        {month && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              {/* left: ring + tiles */}
              <div>
                <div className="flex justify-center mb-4">
                  <Ring pct={month.pct} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Tile label="Present" value={month.present} sub={month.present === 0 ? "no check-ins" : month.late ? `${month.late} late` : "all on time"} tone="border-emerald-200 bg-emerald-50 text-emerald-800" />
                  <Tile label="Absent" value={month.absent} sub="working days missed" tone="border-red-200 bg-red-50 text-red-800" />
                  <Tile label="On leave" value={month.leave} sub="approved" tone="border-sky-200 bg-sky-50 text-sky-800" />
                  <Tile label="Hours worked" value={month.totalHours ? fmtHours(month.totalHours) : "—"} sub={month.avgHours ? `${fmtHours(month.avgHours)} per day` : "no check-outs yet"} tone="border-indigo-200 bg-indigo-50 text-indigo-800" />
                  <Tile label="Avg check-in" value={month.avgIn !== null ? to12h(month.avgIn) : "—"} sub="present days" tone="border-cyan-200 bg-cyan-50 text-cyan-800" />
                  <Tile label="Days counted" value={month.counted} sub="present + absent" tone="border-gray-200 bg-gray-50 text-gray-700" />
                </div>
              </div>

              {/* right: calendar */}
              <div>
              <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                  <div key={i} className="text-center text-[10px] font-bold text-gray-400">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {Array.from({ length: lead }).map((_, i) => <div key={`b${i}`} />)}
                {month.days.map((d) => {
                  const k = KIND[d.kind];
                  const tip = `${new Date(`${d.date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}: ${d.flagged ? "Absent (checked in late)" : k.label}${d.inT ? ` | In ${hhmm(d.inT)}${d.outT ? ` - Out ${hhmm(d.outT)}` : ""}` : ""}${d.hours !== null ? ` | ${fmtHours(d.hours)}` : ""}`;
                  return (
                    <div key={d.date} title={tip} className={`relative aspect-square rounded-lg flex items-center justify-center text-xs font-bold ${k.cell}`}>
                      {d.day}
                      {d.flagged && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-red-500" />}
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
                {["present", "late", "absent", "leave", "off"].map((x) => (
                  <span key={x} className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <span className={`w-2.5 h-2.5 rounded-sm ${KIND[x].dot}`} /> {KIND[x].label}
                  </span>
                ))}
                <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> checked in late, marked Absent
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">Hover or tap a day to see its check-in and check-out.</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
